import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Box } from '@mui/material';

import { getInitiativeById } from '@/features/canvas/api';
import {
  deleteBudgetDropdownVisible,
  getBudgetDropdownsVisible,
} from '@/features/canvas/api/budget-dropdown-visible';
import { getBudgetPopupsVisible } from '@/features/canvas/api/budget-popup-visible';
import { getBudgetRoadmapVisible } from '@/features/canvas/api/budget-roadmap-visible';
import { FieldStyledSelect } from '@/features/canvas/components/StyledSelect';
import { InitiativeFieldTypes } from '@/features/canvas/constants/initiative-field';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useBudgetFields } from '@/features/canvas/hooks/useBudgetFields';
import { InitiativeFieldOption } from '@/features/canvas/types/initiative';
import { useShowToast } from '@/hooks/useShowToast';

type Props = {
  disabled: boolean;
  setCustomFieldType: Dispatch<SetStateAction<'big' | 'small' | null>>;
  setExpandedAccordionKey: Dispatch<SetStateAction<number | null>>;
  setIsCreatingCustomField: Dispatch<SetStateAction<boolean>>;
  initiativeId?: number;
};

const InitiativeBudgetFieldDropdown = ({
  disabled,
  initiativeId,
  setIsCreatingCustomField,
  setCustomFieldType,
  setExpandedAccordionKey,
}: Props) => {
  const { t } = useTranslation();
  const { showToast } = useShowToast();

  const queryClient = useQueryClient();
  const { budgetDropdownVisible, selectedNodeId, roadmap } = useEditorContext();
  const { createField } = useBudgetFields({ initiativeId });

  const [budgetFieldOptions, setBudgetFieldOptions] = useState<InitiativeFieldOption[]>([]);

  useEffect(() => {
    // Create options from budgetDropdownVisible
    const options: InitiativeFieldOption[] = [
      // Add "Create new field" option at the top
      {
        id: 'create-new-big',
        title: t('editor.sidebar.initiative.budget.add_new_field'),
        type: 'big' as const,
        isDeletable: false,
      },
      // Add fields from other roadmaps
      ...budgetDropdownVisible.map(field => ({
        id: `${field.title}-${field.type}`,
        title: field.title,
        type: field.type,
        isDeletable: true,
      })),
    ];

    setBudgetFieldOptions(options);

    console.log('🔍 BCF dropdown options:', options);
  }, [budgetDropdownVisible, t]);

  const onCreateFieldFromDropdown = async (
    fieldTitle: string | null,
    fieldType: keyof typeof InitiativeFieldTypes
  ) => {
    // Ensure the field type is valid for budget fields
    if (fieldType !== 'big' && fieldType !== 'small') return;
    if (fieldTitle === t('editor.sidebar.initiative.budget.add_new_field')) {
      // Create new field mode
      setIsCreatingCustomField(true);
      setCustomFieldType('big');
      setExpandedAccordionKey(0);
      return;
    }

    // Adding field from another roadmap
    const dropdownField = budgetDropdownVisible.find(
      f => f.title === fieldTitle && f.type === fieldType
    );
    if (dropdownField) {
      try {
        await createField({
          dropdownField: {
            title: dropdownField.title,
            type: dropdownField.type,
            id: dropdownField.id,
          },
          field: {
            title: dropdownField.title,
            type: fieldType as 'big' | 'small',
            text_value: '',
          },
        });

        // COPY EXACT ICF PATTERN: Invalidate queries for immediate UI update
        await queryClient.invalidateQueries([getInitiativeById.name, selectedNodeId]);

        showToast('success', t('editor.sidebar.initiative.create_field_success'));
      } catch (error) {
        showToast('error', t('editor.sidebar.initiative.create_field_error'));
      }
    }
  };

  const onDeleteFieldOption = async (fieldTitle: string, fieldType: 'big' | 'small') => {
    console.log('🚀 BCF X button clicked!', { fieldTitle, fieldType });

    // Find the dropdown field to get its ID for permanent deletion
    const dropdownField = budgetDropdownVisible.find(
      f => f.title === fieldTitle && f.type === fieldType
    );

    console.log('🔍 Found dropdown field:', dropdownField);

    if (!dropdownField?.id) {
      return showToast('error', t('editor.sidebar.initiative.budget.delete_field_error'));
    }

    try {
      // Delete the field from ALL roadmap dropdowns (correct endpoint)
      // This removes it from dropdown_visibles across all roadmaps
      // but preserves existing field data in initiatives that already have it
      await deleteBudgetDropdownVisible({
        fieldId: dropdownField.id,
      });

      // Invalidate relevant queries to update the UI (copying ICF pattern)
      await queryClient.invalidateQueries([getInitiativeById.name, selectedNodeId]);
      await queryClient.invalidateQueries([getBudgetDropdownsVisible.name, roadmap.id]);
      await queryClient.invalidateQueries([getBudgetRoadmapVisible.name, roadmap.id]);
      await queryClient.invalidateQueries([getBudgetPopupsVisible.name, roadmap.id]);

      showToast('success', t('editor.sidebar.initiative.budget.delete_field_success'));
    } catch (error) {
      showToast('error', t('editor.sidebar.initiative.budget.delete_field_error'));
    }
  };

  return (
    <Box sx={{ padding: '1rem 0', maxWidth: '100%', minWidth: 120 }}>
      <FieldStyledSelect
        placeholder={t('editor.sidebar.initiative.budget.select_budget_field_placeholder')}
        selectedId={null}
        options={budgetFieldOptions}
        onChange={onCreateFieldFromDropdown}
        onDelete={onDeleteFieldOption}
        disabled={disabled}
      />
    </Box>
  );
};

export default InitiativeBudgetFieldDropdown;
